// ScamLens local auth — a zero-cost, serverless-friendly replacement for
// Netlify Identity with the same API, so the rest of the app is unchanged.
// Accounts live in this browser's localStorage ONLY — no server, no email,
// no credits. Demo-grade security: passwords are hashed (non-cryptographic
// hash, salted) and never leave the device. A real backend can swap this
// module out later without touching app.js.

const USERS_KEY = 'scamlens_users_v1';
const SESSION_KEY = 'scamlens_session_v1';

export const AUTH_EVENTS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',
};

export class AuthError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

export class MissingIdentityError extends AuthError {
  constructor(message = 'Auth is not configured.') {
    super(message, 0);
    this.name = 'MissingIdentityError';
  }
}

const listeners = new Set();
function emit(event, user) {
  listeners.forEach((cb) => {
    try { cb(event, user) } catch { /* listener errors must not break auth */ }
  });
}

export function onAuthChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// cyrb53 — tiny non-cryptographic hash. Keeps passwords out of plaintext on
// this device. NOT a substitute for real password hashing (bcrypt/argon2)
// done server-side.
function hashPw(password, salt) {
  const str = 'scamlens|' + salt + '|' + password;
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

function readUsers() {
  try {
    return JSON.parse(localStorage.getItem(USERS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeUsers(users) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function toUser(email, record) {
  return {
    email,
    name: record.full_name || '',
    picture: record.picture || null,
    user_metadata: { full_name: record.full_name || '', picture: record.picture || null },
  };
}

function sessionEmail() {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    return s && s.email ? String(s.email) : null;
  } catch {
    return null;
  }
}

export async function getUser() {
  const email = sessionEmail();
  if (!email) return null;
  const record = readUsers()[email];
  return record ? toUser(email, record) : null;
}

export async function signup(email, password, data) {
  email = String(email || '').trim().toLowerCase();
  const users = readUsers();
  if (users[email]) throw new AuthError('already registered', 400);
  const full_name = (data && data.full_name) || '';
  const salt = Math.random().toString(36).slice(2) + Date.now().toString(36);
  users[email] = { full_name, salt, pw: hashPw(password, salt), confirmedAt: Date.now() };
  writeUsers(users);
  // Autoconfirmed — there is no email step in the free local version.
  return { confirmedAt: users[email].confirmedAt };
}

export async function login(email, password) {
  email = String(email || '').trim().toLowerCase();
  const record = readUsers()[email];
  if (!record || record.pw !== hashPw(password, record.salt)) {
    throw new AuthError('invalid_grant: password invalid', 401);
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify({ email }));
  const user = toUser(email, record);
  emit(AUTH_EVENTS.LOGIN, user);
  return user;
}

export async function logout() {
  localStorage.removeItem(SESSION_KEY);
  emit(AUTH_EVENTS.LOGOUT, null);
}

export async function updateUser(attrs) {
  const email = sessionEmail();
  if (!email) throw new AuthError('no current user', 401);
  const users = readUsers();
  const record = users[email];
  if (!record) throw new AuthError('no current user', 401);
  let changed = false;
  if (attrs && attrs.password) {
    record.pw = hashPw(attrs.password, record.salt);
    changed = true;
  }
  if (attrs && typeof attrs.full_name === 'string') {
    record.full_name = attrs.full_name.trim();
    changed = true;
  }
  if (changed) writeUsers(users);
  return toUser(email, record);
}

export async function requestPasswordRecovery() {
  throw new AuthError('Password reset by email is not available in the free version.', 400);
}

/* ---------- Google sign-in (Google Identity Services) ---------- */
// The Google button on the login/signup pages returns an ID token (JWT)
// issued by Google for the verified account the user picked in Google's own
// popup. We decode it here and open (or create) the matching local account.
// The token arrives through Google's library, never as user-typed input, and
// the session still lives only on this device — like every other account in
// the free version.
function decodeJwtPayload(token) {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) throw new AuthError('Google sign-in failed: bad token.', 400);
  const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(Array.prototype.map.call(atob(b64), (c) =>
    '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  return JSON.parse(json);
}

export async function loginWithGoogle(credential) {
  let claims;
  try {
    claims = decodeJwtPayload(credential);
  } catch {
    throw new AuthError('Google sign-in failed: bad token.', 400);
  }
  const email = String(claims.email || '').trim().toLowerCase();
  if (!email || claims.email_verified !== true) {
    throw new AuthError('Google sign-in failed: that Google account has no verified email.', 400);
  }
  const users = readUsers();
  const existing = users[email] || null;
  users[email] = {
    full_name: String(claims.name || (existing && existing.full_name) || '').trim(),
    google_sub: claims.sub || (existing && existing.google_sub) || null,
    picture: claims.picture || (existing && existing.picture) || null,
    salt: (existing && existing.salt) || (Math.random().toString(36).slice(2) + Date.now().toString(36)),
    pw: existing ? existing.pw : null, // Google-only accounts have no password
    confirmedAt: (existing && existing.confirmedAt) || Date.now(),
  };
  writeUsers(users);
  localStorage.setItem(SESSION_KEY, JSON.stringify({ email }));
  const user = toUser(email, users[email]);
  emit(AUTH_EVENTS.LOGIN, user);
  return user;
}

export async function acceptInvite() {
  throw new AuthError('Invites are not available in the free version.', 400);
}

export async function handleAuthCallback() {
  // No server-side auth callbacks (OAuth / email confirm / recovery links)
  // exist in the local version.
  return null;
}
